import { FC } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Add, ArrowBack } from '@mui/icons-material';
import { ScheduleDialog } from '@app/components/Admin/Questionnaires/ScheduleDialog';
import { TimepointDialog } from '@app/components/Admin/Questionnaires/TimepointDialog';
import { describeTiming } from '@app/components/Admin/Questionnaires/scheduleDefinitionEditing';
import { useQuestionnaireSchedulesController } from './useQuestionnaireSchedulesController';

/** Every schedule questionnaires can be asked on, with when each timepoint opens and how long it stays open. */
export const AdminQuestionnaireSchedules: FC = () => {
  const schedulesPage = useQuestionnaireSchedulesController();

  if (schedulesPage.loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container component="section" sx={{ py: 2 }}>
      <Button startIcon={<ArrowBack />} onClick={schedulesPage.handleBack} sx={{ mb: 1 }}>
        Back to Questionnaires
      </Button>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4">Schedules</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<Add />} onClick={schedulesPage.handleNewTimepoint}>
            New Timepoint
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={schedulesPage.handleNewSchedule}>
            New Schedule
          </Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A schedule says when a questionnaire is asked, by day of the cohort, where day 1 is the start date. Once a
        cohort uses a schedule, only its name can change.
      </Typography>

      {schedulesPage.loadFailed ? (
        <Alert severity="error">The schedules could not be loaded.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Schedule</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Timepoints</TableCell>
              <TableCell>Used</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {schedulesPage.schedules.map((schedule) => (
              <TableRow key={schedule.id}>
                <TableCell>{schedule.name}</TableCell>
                <TableCell>{schedule.code}</TableCell>
                <TableCell>
                  {schedule.timepoints.map((timepoint) => (
                    <Typography key={timepoint.timepointId} variant="body2">
                      {timepoint.timepointName}: {describeTiming(timepoint.opensOnDay, timepoint.windowDays)}
                    </Typography>
                  ))}
                </TableCell>
                <TableCell>{schedule.inUse ? <Chip size="small" label="In use" color="success" /> : '—'}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => schedulesPage.handleEditSchedule(schedule)}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {!schedulesPage.loadFailed && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Timepoints
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The labels schedules are built from. Results are grouped by timepoint, so they can&apos;t be renamed or
            removed.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timepoint</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>On schedules</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedulesPage.timepointRows.map((timepoint) => (
                <TableRow key={timepoint.id}>
                  <TableCell>{timepoint.name}</TableCell>
                  <TableCell>{timepoint.code}</TableCell>
                  <TableCell>{timepoint.scheduleNames.join(', ') || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <ScheduleDialog
        open={schedulesPage.scheduleDialogOpen}
        schedule={schedulesPage.editingSchedule}
        timepoints={schedulesPage.timepoints}
        onClose={schedulesPage.handleCloseScheduleDialog}
        onSaved={schedulesPage.handleSaved}
      />
      <TimepointDialog
        open={schedulesPage.timepointDialogOpen}
        onClose={schedulesPage.handleCloseTimepointDialog}
        onSaved={schedulesPage.handleSaved}
      />
    </Container>
  );
};
